// Group + sort helpers for the Grouped milestone table view. Pure functions so the table component stays a thin renderer.

import type { Milestone } from "../../../../types";
import {
  deriveMilestoneDisplayStatus,
  type MilestoneDisplayStatusResult,
} from "../../../../utils/deriveMilestoneDisplayStatus";
import { deriveMilestoneDisplayLifecycle } from "../../../../utils/deriveMilestoneDisplayLifecycle";

export type MilestoneGroupKey =
  | "needsAttention"
  | "awaitingAcceptance"
  | "awaitingPayment"
  | "upcoming"
  | "completed";

export const GROUP_ORDER: ReadonlyArray<MilestoneGroupKey> = [
  "needsAttention",
  "awaitingAcceptance",
  "awaitingPayment",
  "upcoming",
  "completed",
];

export const GROUP_TITLE: Record<MilestoneGroupKey, string> = {
  needsAttention: "Needs attention",
  awaitingAcceptance: "Awaiting acceptance",
  awaitingPayment: "Awaiting payment",
  upcoming: "Upcoming",
  completed: "Completed",
};

export interface MilestoneGroup {
  key: MilestoneGroupKey;
  title: string;
  /** Dynamic sub-label describing contents (e.g. "2 late · 1 partially accepted"). */
  subLabel: string;
  items: Milestone[];
}

function isAttentionOverlay(
  display: MilestoneDisplayStatusResult,
): boolean {
  return (
    display.riskOverlay === "late" || display.riskOverlay === "delivered-late"
  );
}

export function placeMilestone(
  m: Milestone,
  today?: string,
): MilestoneGroupKey {
  const display = deriveMilestoneDisplayStatus(m, today);
  // Carve-out: when partial acceptance is recorded but full acceptance is
  // not, the milestone shows as `partially-accepted` even if a partial
  // payment has been recorded — Needs attention is the correct triage
  // bucket regardless of the paid timestamp.
  const lifecycle = deriveMilestoneDisplayLifecycle(m);
  if (lifecycle === "paid") return "completed";
  if (isAttentionOverlay(display)) return "needsAttention";
  if (lifecycle === "partially-accepted") return "needsAttention";
  if (lifecycle === "accepted") return "awaitingPayment";
  if (lifecycle === "delivered") return "awaitingAcceptance";
  return "upcoming";
}

function isoOrEmpty(v: string | null | undefined): string {
  return typeof v === "string" ? v : "";
}

function daysLate(m: Milestone, today: string): number {
  // Mirrors the slipped logic in summarise.ts but inlined to avoid a cycle.
  const planned = m.plannedDate;
  if (!planned) return 0;
  const isoDay = (iso: string) => {
    const d = new Date(`${iso}T00:00:00Z`);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };
  if (m.deliveredAt && m.deliveredAt > planned) {
    return Math.round((isoDay(m.deliveredAt) - isoDay(planned)) / 86_400_000);
  }
  if (!m.deliveredAt && planned < today) {
    return Math.round((isoDay(today) - isoDay(planned)) / 86_400_000);
  }
  return 0;
}

function sortNeedsAttention(a: Milestone, b: Milestone, today: string): number {
  // Slip duration desc, value desc tiebreak.
  const slipDelta = daysLate(b, today) - daysLate(a, today);
  if (slipDelta !== 0) return slipDelta;
  return (b.paymentAmount ?? 0) - (a.paymentAmount ?? 0);
}

function sortAwaitingAcceptance(a: Milestone, b: Milestone): number {
  // deliveredAt desc; missing deliveredAt sinks to the bottom.
  return isoOrEmpty(b.deliveredAt).localeCompare(isoOrEmpty(a.deliveredAt));
}

function sortAwaitingPayment(a: Milestone, b: Milestone): number {
  // acceptedAt desc; missing acceptedAt sinks to the bottom.
  return isoOrEmpty(b.acceptedAt).localeCompare(isoOrEmpty(a.acceptedAt));
}

function sortUpcoming(a: Milestone, b: Milestone): number {
  // plannedDate asc; missing planned date sinks.
  const ap = a.plannedDate ?? "";
  const bp = b.plannedDate ?? "";
  if (ap === bp) return 0;
  if (!ap) return 1;
  if (!bp) return -1;
  return ap.localeCompare(bp);
}

function sortCompleted(a: Milestone, b: Milestone): number {
  return isoOrEmpty(b.paidAt).localeCompare(isoOrEmpty(a.paidAt));
}

export function sortWithinGroup(
  key: MilestoneGroupKey,
  items: Milestone[],
  today: string,
): Milestone[] {
  const copy = [...items];
  if (key === "needsAttention") {
    copy.sort((a, b) => sortNeedsAttention(a, b, today));
  } else if (key === "awaitingAcceptance") {
    copy.sort(sortAwaitingAcceptance);
  } else if (key === "awaitingPayment") {
    copy.sort(sortAwaitingPayment);
  } else if (key === "upcoming") {
    copy.sort(sortUpcoming);
  } else {
    copy.sort(sortCompleted);
  }
  return copy;
}

interface AttentionCounts {
  late: number;
  deliveredLate: number;
  partiallyAccepted: number;
}

function countNeedsAttention(
  items: Milestone[],
  today?: string,
): AttentionCounts {
  let late = 0;
  let deliveredLate = 0;
  let partiallyAccepted = 0;
  for (const m of items) {
    const d = deriveMilestoneDisplayStatus(m, today);
    if (d.riskOverlay === "late") late += 1;
    else if (d.riskOverlay === "delivered-late") deliveredLate += 1;
    else if (deriveMilestoneDisplayLifecycle(m) === "partially-accepted") {
      partiallyAccepted += 1;
    }
  }
  return { late, deliveredLate, partiallyAccepted };
}

function pluralise(noun: string, n: number): string {
  return n === 1 ? noun : `${noun}s`;
}

function buildAttentionSubLabel(counts: AttentionCounts): string {
  const parts: string[] = [];
  if (counts.late > 0) parts.push(`${counts.late} late`);
  if (counts.deliveredLate > 0) parts.push(`${counts.deliveredLate} delivered late`);
  if (counts.partiallyAccepted > 0) {
    parts.push(`${counts.partiallyAccepted} partially accepted`);
  }
  return parts.join(" · ");
}

function buildSubLabel(
  key: MilestoneGroupKey,
  items: Milestone[],
  today?: string,
): string {
  if (items.length === 0) return "";
  if (key === "needsAttention") {
    return buildAttentionSubLabel(countNeedsAttention(items, today));
  }
  if (key === "awaitingAcceptance") {
    return `${items.length} ${pluralise("milestone", items.length)}`;
  }
  if (key === "awaitingPayment") {
    return `${items.length} ${pluralise("milestone", items.length)}`;
  }
  if (key === "upcoming") {
    return `${items.length} ahead`;
  }
  return `${items.length} paid`;
}

export function groupMilestones(
  items: readonly Milestone[],
  today: string,
): MilestoneGroup[] {
  const buckets: Record<MilestoneGroupKey, Milestone[]> = {
    needsAttention: [],
    awaitingAcceptance: [],
    awaitingPayment: [],
    upcoming: [],
    completed: [],
  };
  for (const m of items) {
    buckets[placeMilestone(m, today)].push(m);
  }
  return GROUP_ORDER.map((key) => ({
    key,
    title: GROUP_TITLE[key],
    subLabel: buildSubLabel(key, buckets[key], today),
    items: sortWithinGroup(key, buckets[key], today),
  }));
}
